import cx from 'classnames';
import { type SyntheticEvent, useEffect, useMemo, useState } from 'react';

import Spinner from '../Spinner';

import styles from './image.module.scss';
import type { ImageProps } from './image.types';

const fitMap = {
  'Fit': 'fit',
  'Fill': 'fill',
  'Crop': 'crop',
};

const Image = (props: ImageProps): JSX.Element => {
  const {
    className,
    src,
    fit = 'Fit',
    fallback,
    onLoad,
    onError,
    showLoading = false,
    aspectRatio,
    alt = '',
    ...otherImageProps
  } = props;

  const [imgSrc, setImgSrc] = useState<string | undefined>(src);
  const [imgWidth, setImgWidth] = useState<number | undefined>(undefined);
  const [imgHeight, setImgHeight] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Set the image src and loading state when the src prop changes
  useEffect(() => {
    setImgSrc(src);
    setIsLoading(true);
  }, [src]);

  // Handle the image error
  const handleError = (error: SyntheticEvent<HTMLImageElement>) => {
    // If the image is already the fallback, don't set it again
    if (fallback && imgSrc !== fallback) {
      setImgSrc(fallback);
    }
    onError?.(error);
  };

  // Set the image width and height when the image is loaded
  const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    setImgWidth(e.currentTarget.naturalWidth);
    setImgHeight(e.currentTarget.naturalHeight);
    setIsLoading(false);
    onLoad?.();
  };

  // Calculate the natural aspect ratio of the loaded image
  const naturalAspectRatio = useMemo(() => {
    return imgWidth && imgHeight ? `${imgHeight / imgWidth * 100}%` : undefined;
  }, [imgWidth, imgHeight]);

  // Calculate the aspect padding based on the aspect ratio and fit
  const aspectPadding = useMemo(() => {
    // only use aspectRatio if fit is 'Fit'
    if (fit === 'Fit') {
      const [heightRatio, widthRatio] = aspectRatio?.split(':') || [];
      const width = parseFloat(widthRatio);
      const height = parseFloat(heightRatio);
      // If the aspect ratio is not a number, use the natural aspect ratio
      return (width && height && !isNaN(width) && !isNaN(height))
        ? `${(height / width) * 100}%`
        : naturalAspectRatio;
    }
    return undefined;
  }, [aspectRatio, fit, naturalAspectRatio]);

  return (
    <div
      className={cx(styles.image, className)}
    >
      <div
        className={cx(styles.imageContainer, {
          [styles.aspect]: aspectPadding,
        })}
        style={{
          paddingTop: aspectPadding,
        }}
      >
        <img
          {...otherImageProps}
          src={imgSrc || fallback}
          alt={alt}
          className={cx(styles.content, styles[fitMap[fit]])}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
      {isLoading && showLoading &&
        <div className={cx(styles.loadingIcon)}>
          <Spinner />
        </div>
      }
    </div>
  );
};

export default Image;